/** Every application the envoy made, its record and its fit, and a skip by posting. */

import type { Source } from "../catalogue";
import { getJson, pathSegment, postJson, queryString } from "../client";
import { ENVOY_BASE } from "./base";

export type ApplicationStatus =
    "MATCHED" | "TAILORING" | "NEEDS_YOU" | "APPLIED" | "FAILED" | "SKIPPED" | "CLOSED";

export interface Application {
    application_id: string;
    posting_id: string;
    title: string;
    company: string;
    url: string;
    apply_url?: string | null;
    location: string | null;
    /**
     * The vendor the posting's board is on, as the catalogue spells it, and
     * the board's slug with it. Both or neither. Absent for a page the
     * extension read, for a match made before envoy kept them, and from an
     * envoy older than them.
     */
    posting_source?: string | null;
    posting_board?: string | null;
    status: ApplicationStatus;
    status_reason: string | null;
    keywords_asked: number;
    keywords_covered: number;
    has_resume: boolean;
    resume_id?: string | null;
    applied_by: string | null;
    applied_resume_id?: string | null;
    outcome: string | null;
    queued_by?: string | null;
    queued_at?: string | null;
    ready_at?: string | null;
    applied_at?: string | null;
    created_at: string;
    updated_at: string;
}

export interface ApplicationList {
    applications: Application[];
    /** Given only when another application follows this page. */
    next_cursor?: string | null;
    /**
     * How many the filters give over every page, the cursor aside. Envoy
     * always sends it; an answer from before it did reads as unknown.
     */
    total?: number;
}

/** One thing that happened to an application. `envoy_api::responses::ApplicationEvent`. */
export interface ApplicationEvent {
    /** Its place in the application's record, from one. */
    seq: number;
    /**
     * `MATCHED`, `QUEUED`, `UNQUEUED`, `TAILOR_STARTED`, `TAILORED`,
     * `TAILOR_FAILED`, `BLOCKED`, `FAILED`, `APPLIED`, `SKIPPED`,
     * `CLOSED`, `OUTCOME_RECORDED`, `RETRIED` or `POSTING_PLACED` (the
     * backfill naming the posting's board, which moves nothing).
     */
    kind: string;
    /** The event's own fields, without its kind. */
    payload: unknown;
    occurred_at: string;
}

export interface ApplicationEventList {
    events: ApplicationEvent[];
}

export interface ApplicationFit {
    application_id: string;
    posting_id: string;
    keywords_asked: number;
    keywords_covered: number;
    covered: string[] | null;
    missing: string[] | null;
    baseline_version: number;
    scored_at: string;
}

export type SkipReason = "NOT_A_FIT" | "PAY" | "LOCATION" | "COMPANY" | "ALREADY_APPLIED" | "OTHER";

export const SKIP_REASONS: readonly SkipReason[] = [
    "NOT_A_FIT",
    "PAY",
    "LOCATION",
    "COMPANY",
    "ALREADY_APPLIED",
    "OTHER",
];

export interface ListApplicationsParams {
    status?: ApplicationStatus;
    /** Only the applications to these postings, 200 at most. */
    postingIds?: readonly string[];
    appliedSince?: string;
    appliedBefore?: string;
    /** Only those in the person's queue (true), or only those out of it (false). */
    inQueue?: boolean;
    /**
     * Only those at one company: its vendor, and the part of a board's slug
     * before its first dot. Given together or not at all.
     */
    source?: Source;
    company?: string;
    cursor?: string;
    limit?: number;
}

export function listApplications(params: ListApplicationsParams = {}): Promise<ApplicationList> {
    return getJson(
        `${ENVOY_BASE}/applications${queryString({
            status: params.status,
            posting_id: params.postingIds,
            applied_since: params.appliedSince,
            applied_before: params.appliedBefore,
            in_queue: params.inQueue,
            source: params.source,
            company: params.company,
            cursor: params.cursor,
            limit: params.limit,
        })}`,
    );
}

export async function getApplication(applicationId: string): Promise<Application> {
    return getJson(`${ENVOY_BASE}/applications/${pathSegment(applicationId)}`);
}

export async function getApplicationFit(applicationId: string): Promise<ApplicationFit> {
    return getJson(`${ENVOY_BASE}/applications/${pathSegment(applicationId)}/fit`);
}

/** The application's record, in order. */
export async function getApplicationEvents(applicationId: string): Promise<ApplicationEventList> {
    return getJson(`${ENVOY_BASE}/applications/${pathSegment(applicationId)}/events`);
}

/** Records the application sent by the person, naming the resume that went when one did. */
export async function markApplied(
    applicationId: string,
    resumeId?: string | null,
): Promise<Application> {
    return postJson(
        `${ENVOY_BASE}/applications/${pathSegment(applicationId)}/applied`,
        resumeId ? { resume_id: resumeId } : {},
    );
}

export async function skipApplication(
    applicationId: string,
    reason: SkipReason,
): Promise<Application> {
    return postJson(`${ENVOY_BASE}/applications/${pathSegment(applicationId)}/skip`, {
        reason,
    });
}

/**
 * Declines a posting by the catalogue's id, with or without an
 * application to it yet. The envoy never matches it again.
 */
export async function skipPosting(postingId: string, reason: SkipReason): Promise<Application> {
    return postJson(`${ENVOY_BASE}/postings/${pathSegment(postingId)}/skip`, { reason });
}
